import React, { useEffect, useState, Fragment } from "react";
import { Col, Container, Row, Button } from "react-bootstrap";
import { FaSync } from "react-icons/fa";
import { Shimmer } from "react-shimmer";
import toast from "react-hot-toast";

import { stockItemHeader } from "../../constants/CONSTANT";
import JajotApi from "../../api/JajotApi";
import RecordDataTable from "../RecordDataTable";
import RecordModal from "../common/RecordModal";
import { TopNavigation } from "../layout/TopNavigation";
import { useTallySync } from "../../hooks/useTallySync";
import SyncModal from "../common/SyncModal";
import TaskModal from "../common/TaskModal";

function StockItem() {
  const [records, setRecords] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const { isSyncing, syncStatus, startSync, stopSync } = useTallySync(() => {
    fetchStockItems();
  });

  // Fetch stock items
  useEffect(() => {
    fetchStockItems();
  }, []);

  async function fetchStockItems() {
    try {
      setIsLoading(true);
      const result = await JajotApi.getStockItemRecords();
      if (result?.success) {
        setRecords(result.records || []);
      } else {
        setRecords([]);
        toast.error("No stock items found");
      }
    } catch (err) {
      console.error("Error fetching stock items:", err);
      toast.error("Failed to fetch stock item records");
      setRecords([]);
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <Fragment>
      {/* Top Navigation */}
      <TopNavigation active="Stock Items" />

      {/* Main Content */}
      <Container fluid className="py-3">
        <div className="bg-white shadow-sm rounded p-4">
          {/* Header */}
          <Row className="mb-4 align-items-center">
            <Col>
              <h4 className="fw-bold text-primary mb-0">Stock Item Records</h4>
              <small className="text-muted">
                Manage and view all stock items
              </small>
            </Col>
            <Col xs="auto">
              <Button
                variant="outline-secondary"
                className="shadow-sm py-2 me-2"
                disabled={isLoading || isSyncing}
                onClick={() => startSync("fetch-stock")}
              >
                <FaSync className="me-2" /> Sync with Tally
              </Button>
              <Button
                className="fw-bold shadow-sm px-4 py-2"
                onClick={() => setShowTaskModal(true)}
                disabled={isLoading}
              >
                Add Task
              </Button>
            </Col>
          </Row>

          {/* Table / Loader / Empty State */}
          {isLoading ? (
            <div className="p-3">
              <Shimmer width={600} height={30} className="mb-3" />
              <Shimmer width={-1} height={20} className="mb-2" />
              <Shimmer width={-1} height={20} className="mb-2" />
              <Shimmer width={-1} height={20} />
            </div>
          ) : records.length > 0 ? (
            <RecordDataTable
              tableHeaders={stockItemHeader}
              tableData={records}
              handleRowClick={setSelectedRecord}
              permission={{ update: false, delete: false, history: true }}

            />
          ) : (
            <div className="text-center text-muted py-5">
              <h6>No stock item records found</h6>
            </div>
          )}
        </div>
      </Container>

      {/* Modal */}
      <RecordModal
        show={!!selectedRecord}
        onHide={() => setSelectedRecord(null)}
        record={selectedRecord}
        header={stockItemHeader}
        defaultType="stockitems"
      />
      <SyncModal
        show={isSyncing}
        title="Syncing Stock Item Records..."
        status={syncStatus}
        onHide={stopSync}
      />
      {showTaskModal && (
        <TaskModal
          show={showTaskModal}
          onHide={() => setShowTaskModal(false)}
          records={records}
          header="Stock Item"
        />
      )}
    </Fragment>
  );
}

export default StockItem;
